import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import MicIcon from '@mui/icons-material/Mic';

import MessageContent from './MessageContent';
import { downloadHtmlAsPdf } from '../utils/pdfExport';
import { CHATBOT_HE } from '../chatbotStrings.he';

const shimmer = keyframes`
  100% { transform: translateX(100%); }
`;

const typingDot = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

function TypingIndicator() {
  const dotSx = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    bgcolor: 'primary.main',
    display: 'inline-block',
    animation: `${typingDot} 1.4s infinite ease-in-out`,
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5 }}>
      <Box sx={{ ...dotSx, animationDelay: '0s' }} />
      <Box sx={{ ...dotSx, animationDelay: '0.2s' }} />
      <Box sx={{ ...dotSx, animationDelay: '0.4s' }} />
    </Box>
  );
}

const ShimmerLine = styled('div')(({ delay = '0s', width = '80%' }) => ({
  height: 12,
  borderRadius: 8,
  width,
  background: '#e5e7eb',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    transform: 'translateX(-100%)',
    background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.5), transparent)',
    animation: `${shimmer} 2s infinite`,
    animationDelay: delay,
  },
}));

function SkeletonMessage() {
  const [progress, setProgress] = useState(0);
  const [showDelayMessage, setShowDelayMessage] = useState(false);

  useEffect(() => {
    const avgResponseTime = parseInt(localStorage.getItem('avg_response_time') || '5000', 10);
    const threshold = avgResponseTime * 1.3;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 3;
      });
    }, 200);

    const delayTimer = setTimeout(() => {
      setShowDelayMessage(true);
    }, Math.max(6000, threshold));

    return () => {
      clearInterval(interval);
      clearTimeout(delayTimer);
    };
  }, []);

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
        <AutoAwesomeIcon sx={{ fontSize: 18 }} />
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 560 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }} dir="rtl">
          {CHATBOT_HE.labelAssistant}
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ShimmerLine width="85%" />
            <ShimmerLine width="70%" delay="0.3s" />
            <ShimmerLine width="60%" delay="0.6s" />
          </Box>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 6 }} />
            </Box>
            <Typography sx={{ fontSize: 12, color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
              {Math.round(progress)}%
            </Typography>
          </Box>

          {showDelayMessage && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
              <Typography sx={{ fontSize: 12, color: 'warning.dark' }} dir="rtl">
                {CHATBOT_HE.typingDelay}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

function UserVoiceMessageBubble({ voiceTranscript, audioUrl }) {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.16)',
            border: '1px solid rgba(255,255,255,0.22)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-hidden
        >
          <MicIcon sx={{ fontSize: 22, color: '#fff' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.02 }} dir="rtl">
            {CHATBOT_HE.voiceMessageLabel}
          </Typography>
          {voiceTranscript ? (
            <Typography
              sx={{ fontSize: 13.5, mt: 0.75, lineHeight: 1.45, opacity: 0.95, wordBreak: 'break-word' }}
              dir="auto"
              component="div"
            >
              {voiceTranscript}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12, mt: 0.5, opacity: 0.8 }} dir="rtl">
              {CHATBOT_HE.voiceProcessing}
            </Typography>
          )}
        </Box>
      </Box>
      {audioUrl ? (
        <Box sx={{ mt: 1.5 }}>
          <Box
            component="audio"
            controls
            src={audioUrl}
            sx={{ height: 36, width: '100%', minWidth: 220, maxWidth: 320, borderRadius: 999 }}
          />
        </Box>
      ) : null}
    </>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileUploadCard({ fileName, fileSize, status }) {
  const palette = useMemo(() => {
    if (status === 'error') return { bg: '#fef2f2', border: '#fecaca', iconBg: '#fee2e2', icon: 'error.main' };
    if (status === 'success') return { bg: '#f0fdf4', border: '#bbf7d0', iconBg: '#dcfce7', icon: 'success.main' };
    return { bg: '#fff', border: '#e5e7eb', iconBg: '#eff6ff', icon: 'primary.main' };
  }, [status]);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        bgcolor: palette.bg,
        borderColor: palette.border,
        minWidth: 220,
        maxWidth: 360,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          bgcolor: palette.iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <DescriptionIcon sx={{ color: palette.icon }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>
          {fileName}
        </Typography>
        {fileSize ? (
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
            {formatFileSize(fileSize)}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        {status === 'uploading' ? <CircularProgress size={18} /> : null}
        {status === 'success' ? <CheckCircleIcon sx={{ color: 'success.main' }} /> : null}
        {status === 'error' ? <CancelIcon sx={{ color: 'error.main' }} /> : null}
      </Box>
    </Paper>
  );
}

export default function MessagesBody({ messages, isTyping, messagesEndRef, compact = false }) {
  const hasStreamingAssistant = messages.some(
    (m) => m.type === 'assistant' && m.streaming === true
  );

  const handleDownloadPdf = async (message) => {
    try {
      if (message.htmlContent) await downloadHtmlAsPdf(message.htmlContent, `answer-${message.id}.pdf`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to export PDF:', error);
    }
  };

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', bgcolor: '#f9fafb' }}>
      <Box sx={{ px: compact ? 1.5 : 2, py: compact ? 2 : 2.5, display: 'flex', flexDirection: 'column', gap: compact ? 2 : 2.5 }}>
        {messages.map((message) => {
          const isUser = message.type === 'user';
          return (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                flexDirection: isUser ? 'row-reverse' : 'row',
                gap: 2,
                minWidth: 0,
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isUser ? '#304fff' : '#fff',
                  border: isUser ? 'none' : '1px solid #e5e7eb',
                  color: isUser ? '#fff' : '#111827',
                }}
              >
                {isUser ? <PersonIcon sx={{ fontSize: 18 }} /> : <AutoAwesomeIcon sx={{ fontSize: 18 }} />}
              </Avatar>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }} dir="rtl">
                  {isUser ? CHATBOT_HE.labelYou : CHATBOT_HE.labelAssistant}
                </Typography>

                <Box sx={{ width: '100%', maxWidth: '100%' }}>
                  {message.uploadStatus ? (
                    <FileUploadCard fileName={message.fileName} fileSize={message.fileSize} status={message.uploadStatus} />
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        px: 2,
                        py: 1.5,
                        bgcolor: isUser ? '#304fff' : '#fff',
                        color: isUser ? '#fff' : '#111827',
                        borderColor: isUser ? 'transparent' : 'divider',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                      }}
                    >
                      {message.voiceMessage ? (
                        <UserVoiceMessageBubble
                          voiceTranscript={message.voiceTranscript}
                          audioUrl={message.audioUrl}
                        />
                      ) : message.streaming && !message.content && !message.htmlContent ? (
                        <TypingIndicator />
                      ) : (
                        <MessageContent content={message.content} htmlContent={message.htmlContent} />
                      )}

                      {!isUser && message.htmlContent ? (
                        <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Tooltip
                            title={
                              <span dir="rtl" style={{ display: 'block', textAlign: 'right' }}>
                                {CHATBOT_HE.exportPdf}
                              </span>
                            }
                            slotProps={{ tooltip: { dir: 'rtl', sx: { textAlign: 'right' } } }}
                          >
                            <IconButton
                              onClick={() => handleDownloadPdf(message)}
                              size="small"
                              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : null}

                      {!message.voiceMessage && message.audioUrl ? (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            component="audio"
                            controls
                            src={message.audioUrl}
                            sx={{ height: 36, width: '100%', minWidth: 220, maxWidth: 300, borderRadius: 999 }}
                          />
                        </Box>
                      ) : null}
                    </Paper>
                  )}
                </Box>

                <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.75 }}>
                  {message.timestamp?.toLocaleTimeString?.([], { hour: '2-digit', minute: '2-digit' }) || ''}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {isTyping && !hasStreamingAssistant ? <SkeletonMessage /> : null}

        <div ref={messagesEndRef} />
      </Box>
    </Box>
  );
}

